import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, Grid, Chip, Avatar,
    Tabs, Tab, Divider, Grow, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Card, CardContent,
    IconButton, Tooltip, Collapse, Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ForumIcon from '@mui/icons-material/Forum';
import axios from 'axios';
import API_URL from '../../config';
import TableSortLabel from '@mui/material/TableSortLabel';
import Loading from '../../components/Loading';

function TabPanel({ children, value, index }) {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const toLabel = (key) =>
    key.replace(/^cr493_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const HIDDEN_FIELDS = new Set([
    '@odata.etag', 'versionnumber', 'timezoneruleversionnumber',
    'utcconversiontimezonecode', 'overriddencreatedon', 'importsequencenumber',
]);

const FieldRow = ({ label, value }) => (
    <Box display="flex" gap={1} py={0.5} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 200, fontWeight: 600, flexShrink: 0 }}>
            {label}:
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {value !== null && value !== undefined && value !== ''
                ? String(value)
                : <em style={{ color: '#bbb' }}>—</em>}
        </Typography>
    </Box>
);

export default function ACTSChatBotDetails() {
    const { sessionId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [session, setSession] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [forcePromptings, setForcePromptings] = useState([]);
    const [error, setError] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [expandedRows, setExpandedRows] = useState({});
    const [sortCol, setSortCol] = useState(null);
    const [sortDir, setSortDir] = useState('asc');
    const [qSortCol, setQSortCol] = useState(null);
    const [qSortDir, setQSortDir] = useState('asc');

    const handleSort = (col) => {
        if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortCol(col); setSortDir('asc'); }
    };

    const handleQuoteSort = (col) => {
        if (qSortCol === col) setQSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setQSortCol(col); setQSortDir('asc'); }
    };

    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

    useEffect(() => {
        if (!sessionId) return;
        const fetchAll = async () => {
            try {
                const [sessionRes, questionsRes, quotesRes, forceRes] = await Promise.allSettled([
                    axios.get(`${API_URL}/chatbot/sessions/${sessionId}`, { headers }),
                    axios.get(`${API_URL}/chatbot/sessions/${sessionId}/questions`, { headers }),
                    axios.get(`${API_URL}/chatbot/sessions/${sessionId}/quotes`, { headers }),
                    axios.get(`${API_URL}/chatbot/sessions/${sessionId}/force-prompting`, { headers }),
                ]);
                if (sessionRes.status === 'fulfilled') {
                    setSession(sessionRes.value.data);
                } else {
                    const msg = sessionRes.reason?.response?.data?.error || sessionRes.reason?.message || 'Unknown error';
                    setError(`Failed to load session: ${msg}`);
                }
                if (questionsRes.status === 'fulfilled') {
                    setQuestions(questionsRes.value.data);
                }
                if (quotesRes.status === 'fulfilled') {
                    setQuotes(Array.isArray(quotesRes.value.data) ? quotesRes.value.data : []);
                }
                if (forceRes.status === 'fulfilled') {
                    setForcePromptings(Array.isArray(forceRes.value.data) ? forceRes.value.data : []);
                }
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, [sessionId]);

    const visibleFields = (obj) =>
        Object.entries(obj || {}).filter(([k]) => !HIDDEN_FIELDS.has(k) && !k.startsWith('@'));

    const isDone = session?.cr493_done || session?.cr493_isdone;
    const fullName = session?.cr493_full_name || '—';
    const email = session?.cr493_email_address || '—';
    const mainService = session?.cr493_mainservice || session?.cr493_main_service || '—';
    const country = session?.cr493_country || '—';
    const createdOn = session?.['createdon@OData.Community.Display.V1.FormattedValue']
        || (session?.createdon ? new Date(session.createdon).toLocaleString() : '—');

    if (loading) return <Loading />;

    return (
        <Box sx={{ width: '100%', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            <Grow in timeout={500}>
                <Box>
                    {/* ── Gradient Header ── */}
                    <Paper elevation={0} sx={{
                        mb: 4, mt: 2, p: 4, borderRadius: 4,
                        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                        color: 'white',
                        boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
                        position: 'relative', overflow: 'hidden'
                    }}>
                        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                        <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

                        <Box sx={{ position: 'relative', zIndex: 2 }}>
                            <Button
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate('/chatbot')}
                                sx={{ color: '#90caf9', mb: 2, pl: 0, fontWeight: 'bold', '&:hover': { color: 'white', bgcolor: 'transparent', transform: 'translateX(-4px)' }, transition: 'all 0.2s' }}
                            >
                                Back to ChatBot
                            </Button>

                            {/* Avatar + Name + Badges */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                                <Avatar sx={{
                                    width: 64, height: 64,
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                                    border: '2px solid rgba(255,255,255,0.2)',
                                    flexShrink: 0,
                                }}>
                                    <SmartToyIcon fontSize="large" sx={{ color: 'white' }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h5" fontWeight={900} letterSpacing={0.5} sx={{ mb: 0.75, color: 'white' }}>
                                        {fullName}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <Chip
                                            icon={isDone
                                                ? <CheckCircleIcon sx={{ fontSize: '1rem !important', color: '#fff !important' }} />
                                                : <AccessTimeIcon sx={{ fontSize: '1rem !important', color: '#fff !important' }} />}
                                            label={isDone ? 'Done' : 'Pending'}
                                            size="small"
                                            sx={{ bgcolor: isDone ? 'rgba(76,175,80,0.35)' : 'rgba(255,152,0,0.35)', color: 'white', fontWeight: 700 }}
                                        />
                                        <Chip
                                            icon={<EmailIcon sx={{ fontSize: '1rem !important', color: '#fff !important' }} />}
                                            label={email}
                                            size="small"
                                            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }}
                                        />
                                        <Chip label={mainService} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
                                        {country !== '—' && (
                                            <Chip label={country} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            {/* Stats row */}
                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                {[
                                    { label: 'Open Questions', value: questions.length, icon: <QuestionAnswerIcon sx={{ fontSize: 16 }} /> },
                                    { label: 'Force Prompting', value: forcePromptings.length, icon: <ForumIcon sx={{ fontSize: 16 }} /> },
                                    { label: 'Quotes', value: quotes.length, icon: <ReceiptIcon sx={{ fontSize: 16 }} /> },
                                    { label: 'Created On', value: createdOn, icon: <AccessTimeIcon sx={{ fontSize: 16 }} /> },
                                ].map(stat => (
                                    <Box key={stat.label} sx={{
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                        borderRadius: 2, px: 2, py: 1,
                                        display: 'flex', alignItems: 'center', gap: 1,
                                        border: '1px solid rgba(255,255,255,0.12)',
                                    }}>
                                        {stat.icon}
                                        <Typography variant="caption" sx={{ opacity: 0.8, whiteSpace: 'nowrap' }}>{stat.label}:</Typography>
                                        <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>{stat.value}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Paper>

                    {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                    {/* ── Tabs ── */}
                    <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
                            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}
                                sx={{ '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minHeight: 56 } }}>
                                <Tab icon={<PersonIcon />} iconPosition="start" label="Session Info" />
                                <Tab
                                    icon={<ForumIcon />} iconPosition="start"
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            Force Prompting
                                            <Chip label={forcePromptings.length} size="small" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
                                        </Box>
                                    }
                                />
                                <Tab
                                    icon={<QuestionAnswerIcon />} iconPosition="start"
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            Open Questions
                                            <Chip label={questions.length} size="small" color="secondary" sx={{ height: 20, fontSize: '0.7rem' }} />
                                        </Box>
                                    }
                                />
                                <Tab
                                    icon={<ReceiptIcon />} iconPosition="start"
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            Quotes
                                            <Chip label={quotes.length} size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                                        </Box>
                                    }
                                />
                            </Tabs>
                        </Box>

                        {/* Tab 0: Session Info */}
                        <TabPanel value={tabValue} index={0}>
                            {!session ? (
                                <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No session data found.</Typography>
                            ) : (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                            <CardContent>
                                                <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>Contact Info</Typography>
                                                <Divider sx={{ mb: 1.5 }} />
                                                <FieldRow label="Full Name" value={session.cr493_full_name} />
                                                <FieldRow label="Email Address" value={session.cr493_email_address} />
                                                <FieldRow label="Country" value={session.cr493_country} />
                                                <FieldRow label="Session ID" value={session.cr493_session_id} />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                            <CardContent>
                                                <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>Service & Status</Typography>
                                                <Divider sx={{ mb: 1.5 }} />
                                                <FieldRow label="Main Service" value={session.cr493_mainservice || session.cr493_main_service} />
                                                <FieldRow label="Sub Service" value={session.cr493_subservice || session.cr493_sub_service || session.cr493_subservices} />
                                                <FieldRow label="Done" value={isDone ? 'Yes' : 'No'} />
                                                <FieldRow label="Duration" value={session.cr493_duration} />
                                            </CardContent>
                                        </Card>
                                    </Grid>
           
                                </Grid>
                            )}
                        </TabPanel>

                        {/* Tab 2: Open Questions */}
                        <TabPanel value={tabValue} index={2}>
                            {questions.length === 0 ? (
                                <Box sx={{ py: 6, textAlign: 'center' }}>
                                    <QuestionAnswerIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                                    <Typography color="text.secondary">No open questions linked to this session.</Typography>
                                </Box>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#f4f6f8' }}>
                                                <TableCell sx={{ fontWeight: 700 }}>
                                                    <TableSortLabel active={sortCol === 'idx'} direction={sortCol === 'idx' ? sortDir : 'asc'} onClick={() => handleSort('idx')}>#</TableSortLabel>
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>
                                                    <TableSortLabel active={sortCol === 'question'} direction={sortCol === 'question' ? sortDir : 'asc'} onClick={() => handleSort('question')}>Question</TableSortLabel>
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>
                                                    <TableSortLabel active={sortCol === 'answer'} direction={sortCol === 'answer' ? sortDir : 'asc'} onClick={() => handleSort('answer')}>Answer / Response</TableSortLabel>
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>
                                                    <TableSortLabel active={sortCol === 'created'} direction={sortCol === 'created' ? sortDir : 'asc'} onClick={() => handleSort('created')}>Created On</TableSortLabel>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {[...questions].sort((a, b) => {
                                                if (!sortCol) return 0;
                                                let av, bv;
                                                if (sortCol === 'idx') return 0;
                                                if (sortCol === 'question') {
                                                    av = a.cr493_question || a.cr493_prompt || a.cr493_content || a.cr493_name || '';
                                                    bv = b.cr493_question || b.cr493_prompt || b.cr493_content || b.cr493_name || '';
                                                } else if (sortCol === 'answer') {
                                                    av = a.cr493_answer || a.cr493_response || a.cr493_reply || '';
                                                    bv = b.cr493_answer || b.cr493_response || b.cr493_reply || '';
                                                } else if (sortCol === 'created') {
                                                    av = a.createdon || '';
                                                    bv = b.createdon || '';
                                                }
                                                if (av < bv) return sortDir === 'asc' ? -1 : 1;
                                                if (av > bv) return sortDir === 'asc' ? 1 : -1;
                                                return 0;
                                            }).map((q, idx) => {
                                                const questionText = q.cr493_question || q.cr493_prompt || q.cr493_content || q.cr493_name || '—';
                                                const answerText = q.cr493_answer || q.cr493_response || q.cr493_reply || '—';
                                                const qCreated = q['createdon@OData.Community.Display.V1.FormattedValue']
                                                    || (q.createdon ? new Date(q.createdon).toLocaleString() : '—');

                                                return (
                                                    <React.Fragment key={q.cr493_actschatbot_openquestionid || idx}>
                                                        <TableRow hover sx={{ '&:hover': { bgcolor: '#f8faff' } }}>
                                                            <TableCell>
                                                                <Chip label={idx + 1} size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 700, height: 22 }} />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" fontWeight={500}>{questionText}</Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" color="text.secondary"
                                                                    sx={{ maxWidth: 400, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                    {answerText}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" color="text.secondary">{qCreated}</Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </TabPanel>
                        {/* Tab 1: Force Prompting */}
                        <TabPanel value={tabValue} index={1}>
                            {forcePromptings.length === 0 ? (
                                <Box sx={{ py: 6, textAlign: 'center' }}>
                                    <ForumIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                                    <Typography color="text.secondary">No force prompting records linked to this session.</Typography>
                                </Box>
                            ) : (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                        <ForumIcon sx={{ color: '#0288d1', fontSize: 20 }} />
                                        <Typography variant="subtitle1" fontWeight={700} color="text.primary">Force Prompting</Typography>
                                        <Chip label={forcePromptings.length} size="small" sx={{ bgcolor: '#e1f5fe', color: '#0277bd', fontWeight: 700, height: 22 }} />
                                    </Box>
                                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8eaf0' }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#f4f6f8' }}>
                                                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Full Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Main Service</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Sub Service</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Country</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Session ID</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Done</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Created On</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {forcePromptings.map((fp, idx) => {
                                                    const fpDone = fp.cr493_done || fp.cr493_isdone;
                                                    const fpCreated = fp['createdon@OData.Community.Display.V1.FormattedValue']
                                                        || (fp.createdon ? new Date(fp.createdon).toLocaleString() : '—');
                                                    return (
                                                        <TableRow key={fp.cr493_actschatbot_forcepromptingid || idx} hover sx={{ '&:hover': { bgcolor: '#f8faff' } }}>
                                                            <TableCell>
                                                                <Chip label={idx + 1} size="small" sx={{ bgcolor: '#e1f5fe', color: '#0277bd', fontWeight: 700, height: 22 }} />
                                                            </TableCell>
                                                            <TableCell><Typography variant="body2" fontWeight={600}>{fp.cr493_full_name || '—'}</Typography></TableCell>
                                                            <TableCell><Typography variant="body2" color="text.secondary">{fp.cr493_email_address || '—'}</Typography></TableCell>
                                                            <TableCell><Typography variant="body2">{fp.cr493_mainservice || fp.cr493_main_service || '—'}</Typography></TableCell>
                                                            <TableCell><Typography variant="body2">{fp.cr493_subservice || fp.cr493_sub_service || fp.cr493_subservices || '—'}</Typography></TableCell>
                                                            <TableCell><Typography variant="body2">{fp.cr493_country || '—'}</Typography></TableCell>
                                                            <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{fp.cr493_session_id || '—'}</Typography></TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={fpDone ? 'Done' : 'Pending'}
                                                                    size="small"
                                                                    color={fpDone ? 'success' : 'warning'}
                                                                    sx={{ fontWeight: 600, height: 22, fontSize: '0.7rem' }}
                                                                />
                                                            </TableCell>
                                                            <TableCell><Typography variant="body2" color="text.secondary">{fpCreated}</Typography></TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}
                        </TabPanel>

                        {/* Tab 3: Quotes */}
                        <TabPanel value={tabValue} index={3}>
                            {quotes.length === 0 ? (
                                <Box sx={{ py: 8, textAlign: 'center' }}>
                                    <ReceiptIcon sx={{ fontSize: 56, color: '#e0e0e0', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary" fontWeight={600}>No Quotes</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>No quotes are linked to this session.</Typography>
                                </Box>
                            ) : (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                        <ReceiptIcon sx={{ color: '#1565c0', fontSize: 20 }} />
                                        <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                                            Quotes
                                        </Typography>
                                        <Chip label={quotes.length} size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 700, height: 22 }} />
                                    </Box>
                                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8eaf0' }}>
                                        <Table size="small" sx={{ minWidth: 900 }}>
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#f4f6f8' }}>
                                                    <TableCell sx={{ fontWeight: 700, width: 50 }}>#</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>
                                                        <TableSortLabel active={qSortCol === 'name'} direction={qSortCol === 'name' ? qSortDir : 'asc'} onClick={() => handleQuoteSort('name')}>Name</TableSortLabel>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>
                                                        <TableSortLabel active={qSortCol === 'company'} direction={qSortCol === 'company' ? qSortDir : 'asc'} onClick={() => handleQuoteSort('company')}>Company</TableSortLabel>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>
                                                        <TableSortLabel active={qSortCol === 'type'} direction={qSortCol === 'type' ? qSortDir : 'asc'} onClick={() => handleQuoteSort('type')}>Project Type</TableSortLabel>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>
                                                        <TableSortLabel active={qSortCol === 'location'} direction={qSortCol === 'location' ? qSortDir : 'asc'} onClick={() => handleQuoteSort('location')}>Location</TableSortLabel>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Timeline</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>
                                                        <TableSortLabel active={qSortCol === 'created'} direction={qSortCol === 'created' ? qSortDir : 'asc'} onClick={() => handleQuoteSort('created')}>Created On</TableSortLabel>
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {[...quotes].sort((a, b) => {
                                                    if (!qSortCol) return 0;
                                                    let av = '', bv = '';
                                                    if (qSortCol === 'name')          { av = a.cr493_name || ''; bv = b.cr493_name || ''; }
                                                    else if (qSortCol === 'company')  { av = a.cr493_company || ''; bv = b.cr493_company || ''; }
                                                    else if (qSortCol === 'type')     { av = a.cr493_projecttype || ''; bv = b.cr493_projecttype || ''; }
                                                    else if (qSortCol === 'location') { av = a.cr493_projectlocation || ''; bv = b.cr493_projectlocation || ''; }
                                                    else if (qSortCol === 'created')  { av = a.createdon || ''; bv = b.createdon || ''; }
                                                    if (av < bv) return qSortDir === 'asc' ? -1 : 1;
                                                    if (av > bv) return qSortDir === 'asc' ? 1 : -1;
                                                    return 0;
                                                }).map((q, idx) => {
                                                    const createdOn = q['createdon@OData.Community.Display.V1.FormattedValue']
                                                        || (q.createdon ? new Date(q.createdon).toLocaleString() : '—');
                                                    return (
                                                        <TableRow key={q.cr493_chatbotquoteid || idx} hover sx={{ '&:hover': { bgcolor: '#f8faff' } }}>
                                                            <TableCell>
                                                                <Chip label={idx + 1} size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 700, height: 22 }} />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" fontWeight={600}>{q.cr493_name || '—'}</Typography>
                                                                {q.cr493_projectdescription && (
                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                                                        {q.cr493_projectdescription}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2">{q.cr493_company || '—'}</Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                {q.cr493_projecttype ? (
                                                                    <Chip label={q.cr493_projecttype} size="small"
                                                                        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, height: 22 }} />
                                                                ) : <Typography variant="body2" color="text.secondary">—</Typography>}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" color="text.secondary">{q.cr493_projectlocation || '—'}</Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" color="text.secondary">{q.cr493_projecttimeline || '—'}</Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" color="text.secondary">{q.cr493_phonenumber || '—'}</Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" color="text.secondary">{createdOn}</Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}
                        </TabPanel>
                    </Paper>
                </Box>
            </Grow>
        </Box>
    );
}
